/**
 * Repository Management Section Component
 */

import { RefreshCwIcon, SearchIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { GitlabRepository } from '@/lib/types/gitlab';

import { RepositoryCard } from './RepositoryCard';

export type RepositorySectionProps = {
  repositories: GitlabRepository[];
  filteredRepositories: GitlabRepository[];
  repositorySearchQuery: string;
  setRepositorySearchQuery: (query: string) => void;
  repositoriesLoading: boolean;
  onRefresh: () => void;
  onAddWebhook: (repositoryId: number) => void;
  onRemoveWebhook: (repositoryId: number) => void;
  getWebhookOperation: (
    repositoryId: number,
  ) => 'adding' | 'removing' | undefined;
};

export const RepositorySection = ({
  repositories,
  filteredRepositories,
  repositorySearchQuery,
  setRepositorySearchQuery,
  repositoriesLoading,
  onRefresh,
  onAddWebhook,
  onRemoveWebhook,
  getWebhookOperation,
}: RepositorySectionProps): JSX.Element => (
  <div className="space-y-6">
    <div className="flex flex-row justify-between items-center">
      <div>
        <h2 className="text-2xl font-semibold">Repository Management</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage webhooks for your GitLab repositories
        </p>
      </div>
      <Button onClick={onRefresh} variant="outline">
        <RefreshCwIcon className="h-4 w-4 mr-2" />
        Refresh
      </Button>
    </div>

    {/* Repository search and stats */}
    {repositories.length > 0 && (
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search repositories..."
            value={repositorySearchQuery}
            onChange={e => setRepositorySearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{repositories.length} repositories</span>
          <span>
            {repositories.filter(r => r.hasWebhook).length} with webhooks
          </span>
        </div>
      </div>
    )}

    {/* Repository list */}
    {repositoriesLoading ? (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="bg-background w-full h-32" />
        ))}
      </div>
    ) : repositories.length === 0 ? (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        <div className="space-y-4">
          <div>
            No repositories found. Make sure your GitLab account has access to
            repositories.
          </div>
        </div>
      </div>
    ) : (
      <div className="space-y-4">
        {filteredRepositories.map(repository => (
          <RepositoryCard
            key={repository.id}
            repository={repository}
            webhook={
              repository.hasWebhook
                ? {
                    id: repository.webhookId ?? '',
                    repositoryId: repository.id,
                    url: '',
                    status: 'active' as const,
                    events: [],
                    createdAt: '',
                  }
                : null
            }
            webhookOperation={getWebhookOperation(repository.id)}
            onAddWebhook={() => onAddWebhook(repository.id)}
            onRemoveWebhook={() => onRemoveWebhook(repository.id)}
          />
        ))}

        {filteredRepositories.length === 0 && repositorySearchQuery !== '' && (
          <div className="text-center py-8 text-muted-foreground">
            No repositories match your search query.
          </div>
        )}
      </div>
    )}
  </div>
);
